<template>
    <AdnuntiusPixels
        :rendered-url="content.renderedPixelUrl"
        :visible-url="content.visiblePixelUrl"
        :view-url="content.viewPixelUrl"
        :click-url="content.clickPixelUrl"
        always-visible
        class="mx-3 sm:mx-10 first:ml-0 last:mr-0">
        <template #default="{ click }">
            <CultureAwareRouterLink
                :to="content.brandUrl"
                class="c-brandlogo"
                target="_top"
                :style="brandThemeStyle"
                @click="click">
                <img :src="content.image.url"
                     :alt="content.name"
                     class="c-brandlogo-image c-brandlogo-image__gray"
                     :class="imageCssClass"
                     :width="imageWidth"
                     :height="imageHeight"
                     :style="`aspect-ratio: ${content.image.width / content.image.height}`">
                <span class="sr-only">{{ $dictionary.get('BrandHero.BrandLogo.SeeAllProductsFrom') }} {{ content.name }}</span>
            </CultureAwareRouterLink>
        </template>
    </AdnuntiusPixels>
</template>

<script setup lang="ts">
import { computed, PropType, StyleValue } from 'vue';
import AdnuntiusPixels from '@/project/tracking/adnuntius/AdnuntiusPixels.vue';
import { BrandLogoViewModel } from '@/types/apiServerContract';
import themeService from '@/project/app/themes/theme.service';

const props = defineProps({
    content: {
        type: Object as PropType<BrandLogoViewModel>,
        required: true
    }
});

// To get lighthouse score better, provide width and height on brand logos, but scale it and preserve aspect ratio
const imageHeight = 35;
const imageWidth = computed(() => imageHeight * (props.content?.image?.width ?? 1) / (props.content?.image?.height ?? 1))

const imageCssClass = computed((): string => {
    const isTall = props.content.image.width < props.content.image.height;
    const isSquare = props.content.image.width === props.content.image.height;

    return isTall || isSquare ? 'c-brandlogo-image--max-h' : 'c-brandlogo-image--max-w';
});


const brandThemeStyle = computed((): StyleValue => {
    const isInWhiteTheme = themeService.isInWhiteTheme;

    return {
        ...(isInWhiteTheme && { filter: 'brightness(0) invert(1)' })
    };
});

</script>

<style lang="less">
    .c-brandlogo {
        &:hover {
            .c-brandlogo-image {
                filter: grayscale(0);
                opacity: 1;
            }
        }
    }

    .c-brandlogo-image {
        &.c-brandlogo-image__gray {
            filter: grayscale(1);
            opacity: 0.6;
            transition: filter, opacity .5s ease-out;
        }
        max-height: 2.5rem;
        min-width: 2.5rem;

        &.c-brandlogo-image--max-w {
            max-width: 6rem;
            min-width: 6rem;
            width: 100%;
        }
    }

    @screen sm {
        .c-brandlogo-image {
            &.c-brandlogo-image--max-w {
                max-width: 7.5rem;
                min-width: 7rem;
            }
        }
    }
    @screen md {
        .c-brandlogo-image {
            max-height: 3.5rem;
            min-width: 3.5rem;
            &.c-brandlogo-image--max-w {
                max-width: 7.5rem;
                min-width: 7rem;
                max-height: 5rem;
                width: 100%;
            }
        }
    }
</style>
